import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import {
  RetroBackground,
  RetroButton,
  PALETTE,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const ledText = (size = '13px', color = PALETTE.scoreboardText) => ({
  fontFamily: DESIGN_TOKENS.fonts.display,
  fontSize: size,
  color,
  textShadow: `0 0 8px ${color}88`,
});

export default function InningSummary() {
  const navigate = useNavigate();
  const {
    gameStarted,
    gameEnded,
    teamA,
    teamB,
    isTopInning,
    inningHistory,
    playerStats,
  } = useGame();

  useEffect(() => {
    if (!gameStarted) {
      navigate('/dashboard');
    }
  }, [gameStarted, navigate]);

  useEffect(() => {
    if (gameEnded) {
      navigate('/end-game');
    }
  }, [gameEnded, navigate]);

  const lastHalfInning = inningHistory[inningHistory.length - 1];
  const wasTopInning = lastHalfInning?.half === 'top';
  const completedInning = lastHalfInning?.inning;

  const battingTeamId = lastHalfInning?.battingTeam;
  const battingTeamData = battingTeamId === 'A' ? teamA : teamB;
  const battingTeamPlayers = battingTeamData?.players || [];

  const getPlayerStats = (playerIndex) => {
    const statsKey = `${battingTeamId}-${playerIndex}`;
    return playerStats[statsKey] || { singles: 0, doubles: 0, triples: 0, homeRuns: 0, outs: 0 };
  };

  const handleContinue = () => {
    navigate('/game');
  };

  return (
    <RetroBackground variant="greenStripes" className="p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6" data-testid="inning-summary-header">
          <h1 style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displayMd,
            color: PALETTE.cream,
            textShadow: `2px 2px 0px ${PALETTE.ink}, 4px 4px 0px rgba(0,0,0,0.4)`,
            letterSpacing: '2px',
            marginBottom: '8px',
          }}>
            INNING SUMMARY
          </h1>
          <div className="flex items-center justify-center gap-3">
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '11px',
              color: PALETTE.cream,
              textShadow: `2px 2px 0px ${PALETTE.ink}`,
              letterSpacing: '1px',
            }}>
              {wasTopInning ? 'TOP' : 'BOTTOM'} OF {completedInning}
            </p>
            {/* LED score chip */}
            <div
              style={{
                background: PALETTE.scoreboardBg,
                border: `3px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.sm,
                padding: '4px 12px',
              }}
              data-testid="inning-summary-score"
            >
              <span style={ledText('14px')}>
                {teamA.score} - {teamB.score}
              </span>
            </div>
          </div>
        </div>

        {/* Box Score - black LED panel per reference */}
        <div
          className="mb-6 p-4"
          style={{
            background: PALETTE.scoreboardBg,
            border: `4px solid ${PALETTE.ink}`,
            borderRadius: DESIGN_TOKENS.borderRadius.md,
            boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
          }}
          data-testid="box-score-panel"
        >
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `2px solid ${PALETTE.scoreboardText}55` }}>
                <th className="text-left py-2" style={ledText('10px')}>PLAYER</th>
                <th className="text-center py-2" style={ledText('10px')}>1B</th>
                <th className="text-center py-2" style={ledText('10px')}>2B</th>
                <th className="text-center py-2" style={ledText('10px')}>3B</th>
                <th className="text-center py-2" style={ledText('10px')}>HR</th>
              </tr>
            </thead>
            <tbody>
              {battingTeamPlayers.map((player, index) => {
                const stats = getPlayerStats(index);
                return (
                  <tr key={index} style={{ borderBottom: `1px solid ${PALETTE.scoreboardText}22` }}>
                    <td className="text-left py-2" style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#FFFFFF',
                    }}>{player}</td>
                    <td className="text-center py-2" style={ledText('12px')}>{stats.singles}</td>
                    <td className="text-center py-2" style={ledText('12px')}>{stats.doubles}</td>
                    <td className="text-center py-2" style={ledText('12px')}>{stats.triples}</td>
                    <td className="text-center py-2" style={ledText('12px')}>{stats.homeRuns}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Inning Highlights */}
        {lastHalfInning?.events && lastHalfInning.events.length > 0 && (
          <div
            className="mb-6"
            style={{
              background: PALETTE.cream,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
              overflow: 'hidden',
            }}
            data-testid="inning-highlights-panel"
          >
            <div
              className="text-center py-2"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '11px',
                color: PALETTE.cream,
                background: PALETTE.grass,
                borderBottom: `3px solid ${PALETTE.ink}`,
                letterSpacing: '1px',
              }}
            >
              INNING HIGHLIGHTS
            </div>
            <div
              className="m-3 p-3 space-y-2"
              style={{
                background: PALETTE.scoreboardBg,
                border: `3px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.sm,
              }}
            >
              {lastHalfInning.events.map((event, index) => {
                const isOut = event.result === 'OUT';
                return (
                  <div key={index} className="flex justify-between items-center gap-2">
                    <span style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontWeight: 600,
                      fontSize: '13px',
                      color: '#FFFFFF',
                    }}>
                      {event.batter}
                    </span>
                    <span style={ledText('10px', isOut ? '#FF3B3B' : PALETTE.scoreboardText)}>
                      {event.result}
                      {event.runsScored > 0 && ` +${event.runsScored}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Continue Button */}
        <RetroButton
          variant="primary"
          size="md"
          fullWidth
          onClick={handleContinue}
          data-testid="continue-btn"
        >
          {isTopInning ? 'START BOTTOM OF INNING' : 'START NEXT INNING'}
        </RetroButton>
      </div>
    </RetroBackground>
  );
}
